import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, Pencil, LogOut, LayoutDashboard, Gauge } from "lucide-react";
import aramoonLogo from "@/assets/aramoon-logo.png.asset.json";
import gabcleLogo from "@/assets/gabcle-logo.png.asset.json";
import tenantHeroBg from "@/assets/tenant-hero-bg.png.asset.json";

const NRW_APP_URL = "https://nrw-insight-flow.lovable.app";

const DEFAULT_TENANT_LOGOS: Record<string, string> = {
  aramoon: aramoonLogo.url,
  gabcle: gabcleLogo.url,
};

function defaultLogoFor(name: string): string | null {
  const key = name.trim().toLowerCase();
  const match = Object.keys(DEFAULT_TENANT_LOGOS).find((k) => key.includes(k));
  return match ? DEFAULT_TENANT_LOGOS[match] : null;
}

interface TenantHomepage {
  hero_title: string | null;
  hero_subtitle: string | null;
  hero_description: string | null;
  logo_url: string | null;
  hero_image_url: string | null;
  body_markdown: string | null;
  primary_color: string | null;
  secondary_color: string | null;
}

interface SiteTheme {
  logoUrl?: string;
  heroImageUrl?: string;
  heroTitleEn?: string;
  heroTaglineEn?: string;
  heroSubTaglineEn?: string;
  headerTitleEn?: string;
  headerBackground?: string;
  headerText?: string;
  backgroundColor?: string;
  foregroundColor?: string;
  cornerRadius?: number;
}

export function TenantHome() {
  const { signOut, user } = useAuth();
  const { currentTenant: activeTenant, tenants, currentRole, isSuperAdmin, setCurrentTenant } = useTenant();
  const currentTenant = activeTenant ?? tenants[0] ?? null;
  const navigate = useNavigate();
  const [data, setData] = useState<TenantHomepage | null>(null);
  const [theme, setTheme] = useState<SiteTheme | null>(null);
  const [loading, setLoading] = useState(true);

  const canEdit = currentRole === "tenant_admin";

  const tenantId = currentTenant?.id;

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const [{ data: row }, { data: themeRow }] = await Promise.all([
        (supabase as any)
          .from("tenant_homepages")
          .select("hero_title,hero_subtitle,hero_description,logo_url,hero_image_url,body_markdown,primary_color,secondary_color")
          .eq("tenant_id", tenantId)
          .maybeSingle(),
        (supabase as any)
          .from("tenant_themes")
          .select("theme_data")
          .eq("tenant_id", tenantId)
          .maybeSingle(),
      ]);
      if (!cancelled) {
        setData(row ?? null);
        setTheme((themeRow?.theme_data as SiteTheme) ?? null);
        setLoading(false);
      }
    };

    load();

    // Always stay in sync with the site theme managed in the Super Admin Control Center
    const channel = supabase
      .channel(`tenant-theme-${tenantId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "tenant_themes", filter: `tenant_id=eq.${tenantId}` },
        () => { load(); }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [tenantId]);

  if (!currentTenant) return null;

  const subtitle = data?.hero_subtitle || theme?.heroTaglineEn || "Your organization workspace";
  const heroTitle = data?.hero_title || theme?.heroTitleEn || "Welcome";
  const heroDescription = data?.hero_description || theme?.heroSubTaglineEn || null;

  const logo = data?.logo_url || theme?.logoUrl || defaultLogoFor(currentTenant.name);
  const heroImage = data?.hero_image_url || theme?.heroImageUrl || tenantHeroBg.url;
  const primary = data?.primary_color || theme?.headerBackground || undefined;
  const headerText = theme?.headerText || undefined;
  const secondary = data?.secondary_color || undefined;


  return (
    <div
      className="min-h-screen flex flex-col bg-background bg-cover bg-center bg-no-repeat bg-fixed text-primary-foreground"
      style={{ backgroundImage: `url(${heroImage})` }}
    >
      <Helmet>
        <title>{`${currentTenant.name} — Homepage`}</title>
        <meta name="description" content={subtitle} />
      </Helmet>

      <header
        className="sticky top-0 z-50 border-b backdrop-blur"
        style={{ background: primary || "hsl(var(--primary))", color: headerText }}
      >
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-2">
            {logo ? (
              <img src={logo} alt={`${currentTenant.name} logo`} className="h-9 w-auto object-contain" />
            ) : (
              <div className="h-9 w-9 rounded-md bg-primary-foreground/15 border border-primary-foreground/30 flex items-center justify-center text-primary-foreground text-sm font-bold">
                {currentTenant.name.slice(0, 2).toUpperCase()}
              </div>
            )}
            <span className="text-xl font-bold text-primary-foreground tracking-tight" style={{ color: headerText }}>
              {theme?.headerTitleEn || currentTenant.name}
            </span>
          </div>


          <div className="flex items-center gap-2">
            {isSuperAdmin && tenants.length > 0 && (
              <Select
                value={currentTenant.id}
                onValueChange={(id) => {
                  const t = tenants.find((x) => x.id === id);
                  if (t) setCurrentTenant(t);
                }}
              >
                <SelectTrigger className="w-auto min-w-[140px] max-w-[180px] h-9 bg-primary-foreground text-primary border-0 shadow-md hover:bg-primary-foreground/90 focus:ring-2 focus:ring-primary-foreground/50 [&>span]:truncate">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent
                  align="end"
                  sideOffset={4}
                  position="popper"
                  className="bg-popover text-popover-foreground border-border shadow-xl"
                >
                  {tenants.map((t) => (
                    <SelectItem
                      key={t.id}
                      value={t.id}
                      className="text-foreground focus:bg-primary focus:text-primary-foreground cursor-pointer"
                    >
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {canEdit && (
              <Button
                size="sm"
                variant="outline"
                className="gap-2 rounded-2xl text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/10 hover:text-primary-foreground"
                onClick={() => navigate("/tenant-home/edit")}
              >
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="gap-2 text-primary-foreground hover:bg-primary-foreground/10"
              onClick={async () => { await signOut(); navigate("/login", { replace: true }); }}
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <section
        className="relative overflow-hidden flex-1 flex items-center justify-center bg-cover bg-center bg-no-repeat bg-fixed"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="container relative py-8">
          <div className="mx-auto max-w-3xl text-center space-y-4">
            <h1 className="text-4xl font-bold tracking-tight text-primary-foreground sm:text-5xl md:text-6xl leading-tight">
              {loading ? " " : heroTitle}
            </h1>
            <p className="text-2xl font-semibold text-primary-foreground sm:text-3xl md:text-4xl">
              {loading ? " " : (theme?.heroTaglineEn || currentTenant.name)}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-16">
              <Button asChild size="lg" className="gap-2 rounded-2xl w-full sm:w-auto bg-offwhite text-foreground hover:bg-muted shadow-lg shadow-black/30 hover:shadow-xl hover:shadow-black/40 transition-shadow">
                <Link to="/dashboard">
                  <LayoutDashboard className="h-5 w-5 text-primary" />
                  DMA Data Capture
                  <ArrowRight className="h-4 w-4 text-primary" />
                </Link>
              </Button>
              <Button asChild size="lg" className="gap-2 rounded-2xl w-full sm:w-auto bg-offwhite text-foreground hover:bg-muted shadow-lg shadow-black/30 hover:shadow-xl hover:shadow-black/40 transition-shadow">
                <Link to="/nrw-monitoring">
                  <Gauge className="h-5 w-5 text-primary" />
                  NRW Monitoring
                  <ArrowRight className="h-4 w-4 text-primary" />
                </Link>
              </Button>
            </div>
          </div>

          {data?.body_markdown && (
            <div className="mx-auto max-w-3xl mt-12 prose prose-invert text-primary-foreground prose-headings:text-primary-foreground prose-p:text-primary-foreground prose-a:text-primary-foreground prose-strong:text-primary-foreground prose-li:text-primary-foreground">
              <ReactMarkdown>{data.body_markdown}</ReactMarkdown>
            </div>
          )}
        </div>
      </section>

      <footer className="mt-auto">
        <div className="container py-6 text-center text-sm text-primary-foreground">
          © {new Date().getFullYear()} {currentTenant.name}
        </div>
      </footer>

    </div>
  );
}
